import { get } from '../request';
import { toPage, type Page, type PageQuery } from './types';

/** 转写分段（时间单位为毫秒） */
export interface TranscriptSegment {
  id?: number;
  speaker: string; // customer|agent|robot
  content: string;
  start_ms: number;
  end_ms: number;
}

/** AI 调用详细日志（通话中 ASR/LLM/TTS 等各阶段时间节点） */
export interface AICallLog {
  id: number;
  cdr_id: number;
  uuid: string;
  seq: number;
  stage: string;
  content: string;
  detail: string;
  offset_ms: number;
  /** 说话/播报起止（录音轴毫秒，相对接通时刻，与 transcript_segment 同轴）：
   *  asr_sentence_end 行=客户说话起止；
   *  tts_turn 行=整轮播报区间（首段出声 → 末段结束/被打断）、tts_segment 行=该段播报区间，均自带无需配对；
   *  旧口径 tts_first_frame 行填 begin_ms、tts_end 行填 end_ms，须两行配对还原播报起止（仅历史通话行如此）；
   *  0=该阶段无语音语义或历史行无该数据（不显示起止）。 */
  begin_ms?: number;
  end_ms?: number;
  event_time: string;
  [k: string]: any;
}

/** 通话参与人：除了承办坐席，组长/值班长/专家等中途介入的人也记在这里 */
export type CallParticipantRole = 'agent' | 'leader' | 'duty' | 'expert' | 'other';
export type CallParticipantAction = 'answer' | 'listen' | 'whisper' | 'three_way' | 'transfer' | 'consult';

export interface CallParticipant {
  /** 参与人姓名 */
  name: string;
  /** 角色：承办坐席 / 组长 / 值班长 / 专家 / 其他 */
  role: CallParticipantRole;
  /** 参与方式：承办 / 监听 / 耳语 / 强插 / 接管 / 咨询 */
  action: CallParticipantAction;
  /** 加入/结束时间（可选；列表先不展示，详情页可补） */
  joined_at?: string;
  left_at?: string;
}

export interface Cdr {
  id: number;
  direction: string; // inbound|outbound
  caller: string;
  callee: string;
  /** 原始被叫（首次拨入号码；转接后 callee 为转接目的地，如 survey/bcc_ivr_to_ai） */
  original_callee?: string;
  agent_name?: string;
  agent_extension?: string;
  /** 坐席 ID：AI 会话转人工时同时有 ai_session + agent_id，会话类型标注为「AI&人工」 */
  agent_id?: number | null;
  /** 参与人（承办坐席 + 监听/强插/接管/咨询的组长、值班长、专家等）：后端未返回时列表按承办坐席兜底 */
  participants?: CallParticipant[];
  ai_session?: boolean;
  /** IVR 满意度外呼通话（ob_ivr_survey 落点）：会话类型标注为「IVR」 */
  ivr_survey?: boolean;
  hangup_cause?: string;
  duration?: number;
  /** 计费时长（应答→挂断，秒）：录音从接听时开始，故录音时长取 billsec 而非 duration（后者含振铃） */
  billsec?: number;
  satisfaction_score?: number | null;
  start_time?: string;
  end_time?: string;
  /** 接通时刻（未接通为 null；用于「正常挂断」误标纠正：answer_time 为空且 NORMAL_CLEARING → 未接通） */
  answer_time?: string | null;
  record_url?: string;
  transcript?: TranscriptSegment[];
  [k: string]: any;
}

export interface CdrQuery extends PageQuery {
  direction?: string;
  caller?: string;
  /** 被叫（含转接场景的原始被叫） */
  callee?: string;
  /** 记录 ID 精确匹配 */
  id?: string;
  /** 通话 UUID 精确匹配 */
  uuid?: string;
  /** 会话类型过滤：aiSession=true AI 会话 / false 纯人工 / 空=全部 */
  aiSession?: string;
  agent_id?: number;
  /** 号码模糊检索（主叫/被叫都匹配）：真实接口待补，前端先按查询参数传递 */
  number?: string;
  /** 队列名（精确匹配，报表队列下钻） */
  queue?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
}

/** GET /cdrs */
export async function listCdrs(q: CdrQuery): Promise<Page<Cdr>> {
  return toPage<Cdr>(await get('/cdrs', q));
}

/** GET /cdrs/:id（详情内嵌 transcript/record_url；silent：不存在/未接通时调用方自行展示空态） */
export async function getCdr(id: number | string): Promise<Cdr> {
  const d = await get(`/cdrs/${id}`, undefined, { silent: true });
  // 后端详情响应为嵌套结构 { cdr, agent_name, transcript, record_url, ticket_id, ... }，
  // CDR 字段（direction/hangup_cause/caller/start_time/duration 等）在 cdr 子对象内；
  // 拉平到顶层，保证与列表项字段契约（type Cdr）一致，否则详情页各字段无法回显。
  const { cdr, ...rest } = (d || {}) as Cdr & { cdr?: Cdr };
  return { ...(cdr || {}), ...rest } as Cdr;
}

/**
 * GET /cdrs/:id/transcript → { transcript: [...] }
 * 来电弹屏纪要用；cdr 不存在时调用方静默忽略（silent）。
 */
export function getCdrTranscript(id: number | string): Promise<{ transcript: TranscriptSegment[] }> {
  return get(`/cdrs/${id}/transcript`, undefined, { silent: true });
}

/** GET /cdrs/:id/ai-logs → { logs: AICallLog[] }（AI 会话详细时间日志，人工会话为空） */
export function getAILogs(id: number | string): Promise<{ logs: AICallLog[] }> {  return get(`/cdrs/${id}/ai-logs`, undefined, { silent: true });
}

/** 流程轨迹（CFO：流程实例/节点路径/事件时间线） */
export interface FlowInstanceView {
  flow_id: number;
  flow_name?: string;
  flow_version: number;
  channel?: string;
  state: string;
  current_node_key?: string;
  resume_until?: string;
  started_at: string;
}
export interface FlowEventView {
  event: string;
  node_key?: string;
  node_name?: string;
  flow_name?: string;
  payload?: string;
  ts: string;
}
/** 单流程实例的完整轨迹 */
export interface FlowInstanceDetail {
  instance: FlowInstanceView;
  events: FlowEventView[];
}
export interface FlowTrace {
  instances: FlowInstanceDetail[];
}

/** GET /cdrs/:id/flow → 流程轨迹（一次通话可先后进入多个流程：子流程/意图再入；空数组=未进入） */
export function getFlowTrace(id: number | string): Promise<FlowTrace> {  return get(`/cdrs/${id}/flow`, undefined, { silent: true });
}

/** 流程状态 → 中文标签 */
export const FLOW_STATE_LABELS: Record<string, string> = {
  running: '进行中',
  suspended: '已挂起',
  completed: '已完成',
  aborted: '已放弃',
  transferred: '已转人工',
  abandoned: '已回收',
};

/** FreeSWITCH hangup_cause → 中文映射（未知原因原样展示）。集中标签层在 utils/labels.ts，此处 re-export 兼容旧 import。 */
export { HANGUP_CAUSE_LABELS, hangupCauseLabel } from '../utils/labels';
